import { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { ReceivementHeader } from '../../components/ReceivementHeader'
import { ReceivementInfo } from '../../components/ReceivementInfo'
import { DivergentProductTable } from '../../components/ReceivementTables/ReceivementFinishTable/DivergentProductTable'
import { CorrectProductTable } from '../../components/ReceivementTables/ReceivementFinishTable/CorrectProductTable'
import { toast } from 'react-toastify'
import { ReceivementFinishFilters } from '../../components/ReceivementFinishFilters'
import { Watch } from '../../components/Watch/Watch'
import { actions } from '../../ReceivementState'
import { actions as swapOrReturnActions } from '../../../swap-or-return/SwapOrReturnState'
import { useDispatch } from 'react-redux'
import { selectFirstObservationPosition } from '../../ReceivementSelectors'
import { useTheme } from 'styled-components'
import { Finish, ReceivementCategoryEnum, ReceivementStatus } from '../../ReceivementTypes'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { Typography } from '@mui/material'
import { useReceivement } from 'src/hooks/useReceivement'
import ReceivementCard from 'src/components/ReceivementCard'
import PreviousPendingRequestForm from '../../components/PreviousPendingRequestForm/PreviousPendingRequestForm'
import PendingRequestsAlert from '../../components/PendingRequestsAlert/PendingRequestsAlert'
import ResolveSwapOrReturnContainer from 'src/features/swap-or-return/components/ResolveSwapOrReturnContainer'
import TagPrinterManagerPerBlock from 'src/components/TagPrinterManagerPerBlock/TagPrinterManagerPerBlock'
import SwapOrReturnTag from 'src/features/swap-or-return/components/SwapOrReturnTag'
import * as S from './ReceivementFinish.styles'
import ModalAlert from 'src/components/ModalAlert'

const ReceivementFinish = () => {
  const [isResolvingRequests, setIsResolvingRequests] = useState(false)
  const [postponePendingRequests, setPostponePendingRequests] = useState(false)
  const [alreadyPrinted, setAlreadyPrinted] = useState(false)
  const [showTagPrinterModal, setShowTagPrinterModal] = useState(false)
  const [disableConfirmTruckDriver, setDisableConfirmTruckDriver] = useState(false)
  const [driverConfirmationSent, setDriverConfirmationSent] = useState(false)

  const { previousPendingRequestInfo } = useSelector((state) => state.receivement)
  const { swapOrReturnToResolveIds } = useSelector((state) => state.swapOrReturn)
  const { receivementId } = useParams<{ receivementId?: string }>()
  const { fc, validateFc } = useFulfillmentCenter()
  const { operatorBadge } = useReceivement({ valideOperator: true })

  const productDescription = useSelector((state) => state.receivement.overviewFilters.productDescription)
  const finish = useSelector((state) => state.receivement.finish)
  const firstObservationPosition = useSelector(selectFirstObservationPosition)
  const history = useHistory()
  const theme = useTheme()
  const dispatch = useDispatch()

  const finishReceivement = useCallback(() => {
    if (!Number.isNaN(firstObservationPosition)) {
      if (!productDescription) {
        dispatch(actions.updateResetObservations(true))
        setTimeout(() => { window.scrollTo(0, firstObservationPosition) }, 0)
      }
      toast.warn('Limpe os filtros e visualize todas as anotações para finalizar!')
      return
    }

    if (finish?.isRelease) {
      toast.success('Recebimento finalizado com sucesso!')
    }

    history.push('/receivement')
  }, [firstObservationPosition, finish?.isRelease, productDescription, dispatch, history])

  const confirmTruckDriver = useCallback((isAvailable: boolean) => {
    if (disableConfirmTruckDriver) {
      return
    }

    setDisableConfirmTruckDriver(true)

    dispatch(actions.confirmTruckDriver({ idReceivement: receivementId, isAvailable, setDisableConfirmTruckDriver }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, receivementId])

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    dispatch(actions.clear())
  }, [dispatch])

  useEffect(() => {
    if (finish?.idSupplier) {
      dispatch(actions.getPreviousPendingRequests({
        idSupplier: finish.idSupplier,
        idFulfillmentCenter: fc.id,
      }))
    }
  }, [dispatch, finish?.idSupplier, fc.id])

  useEffect(() => {
    if (finish?.receivementStatus === ReceivementStatus.DIVERGENCE_FINISHED) {
      history.push(`/receivement/${receivementId}/overview`)
    }

    const interval = setInterval(() => {
      if (!receivementId || !driverConfirmationSent) return

      dispatch(
        actions.getFinish({
          badge: operatorBadge,
          receivementId,
          optionalActionOnSuccess: (data?: Finish) => {
            if (data && data.receivementStatus !== ReceivementStatus.PROCESSING_TRUCK_DRIVER_CONFIRMATION) setDriverConfirmationSent(false)
          },
        }),
      )
    }, 2000)

    return () => {
      clearInterval(interval)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, driverConfirmationSent, finish?.receivementStatus, operatorBadge, receivementId])

  const resolveHeaderButton = () => {
    if (finish) {
      return (
        <S.ButtonsWrapper>
          <S.Button disabled={finish?.requestsTagsInfo?.length === 0} onClick={() => setShowTagPrinterModal(true)}>Imprimir etiquetas</S.Button>
          <S.Button disabled={finish?.requestsTagsInfo?.length > 0 && !alreadyPrinted} onClick={finishReceivement}>Finalizar</S.Button>
        </S.ButtonsWrapper>
      )
    }
    return null
  }

  useEffect(() => {
    localStorage.setItem('idReceivementFromFinish', receivementId || '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const receivementFinished = finish?.receivementStatus === ReceivementStatus.FINISHED
    || finish?.receivementStatus === ReceivementStatus.FAILED_WITH_EXTERNAL_SOURCES
    || finish?.receivementStatus === ReceivementStatus.WAITING_TRUCK_DRIVER_CONFIRMATION
    || finish?.receivementStatus === ReceivementStatus.PROCESSING_TRUCK_DRIVER_CONFIRMATION
    || finish?.receivementStatus === ReceivementStatus.SENDING_INVOICES_TO_FINANCES

  const stopResolution = () => {
    dispatch(swapOrReturnActions.setProperty({ property: 'swapOrReturnToResolveIds', value: [] }))
    setIsResolvingRequests(false)
  }

  const tags = finish?.requestsTagsInfo?.map((b) => <SwapOrReturnTag obj={{ ...b, printed: false }} />)

  if (!finish || !receivementFinished || (previousPendingRequestInfo.requests.length > 0 && !postponePendingRequests)) {
    return (
      <S.ContainerWatch>
        <Watch statusText={finish?.receivementStatusDescription}/>

        {
          !postponePendingRequests && !isResolvingRequests && previousPendingRequestInfo.requests.length > 0 && (
            <PreviousPendingRequestForm
              supplierName={previousPendingRequestInfo.supplierName}
              previousPendingRequests={previousPendingRequestInfo.requests}
              totalSelectedRequests={swapOrReturnToResolveIds.length}
              setPostponePendingRequests={setPostponePendingRequests}
              setIsResolvingRequests={setIsResolvingRequests}
            />
          )
        }

        {
          postponePendingRequests && !isResolvingRequests && previousPendingRequestInfo.requests.length > 0 && (
            <PendingRequestsAlert setPostponePendingRequests={setPostponePendingRequests} />
          )
        }

        {
          isResolvingRequests && (
            <ResolveSwapOrReturnContainer
              useAs='component'
              stopResolution={stopResolution}
            />
          )
        }
      </S.ContainerWatch>
    )
  }

  if (receivementFinished
    && [ReceivementCategoryEnum.EXTERNAL_PURCHASE, ReceivementCategoryEnum.TRANSFER].includes(finish?.idReceivementCategory)
  ) {
    let type = ''
    if (finish?.idReceivementCategory === ReceivementCategoryEnum.EXTERNAL_PURCHASE) {
      type = 'compras externas'
    } else if (finish?.idReceivementCategory === ReceivementCategoryEnum.TRANSFER) {
      type = 'transferência'
    }

    return (
      <ModalAlert
        show
        action={() => history.push('/receivement')}
        title='Recebimento Finalizado'
        message={`Recebimento de ${type} finalizado!`}
        centered
        testId='finished-modal-test-id'
      />
    )
  }

  if (finish?.receivementStatus === ReceivementStatus.WAITING_TRUCK_DRIVER_CONFIRMATION) {
    return (
      <S.ContainerTruckDriver>
        Indique se o fornecedor ainda está no pátio
        <S.ButtonsWrapper>
          <S.ButtonTruckDriver
            background={theme.colors.cancel}
            onClick={() => {
              setDriverConfirmationSent(true)
              confirmTruckDriver(false)
            }}
            disabled={disableConfirmTruckDriver}
            data-testid='truck-driver-not-in-courtyard'
          >
            Fornecedor não está no pátio
          </S.ButtonTruckDriver>
          <S.ButtonTruckDriver
            background={theme.colors.confirm}
            onClick={() => {
              setDriverConfirmationSent(true)
              confirmTruckDriver(true)
            }}
            disabled={disableConfirmTruckDriver}
            data-testid='truck-driver-in-courtyard'
          >
            Fornecedor está no pátio
          </S.ButtonTruckDriver>
        </S.ButtonsWrapper>
      </S.ContainerTruckDriver>
    )
  }

  if (driverConfirmationSent || finish?.receivementStatus === ReceivementStatus.PROCESSING_TRUCK_DRIVER_CONFIRMATION) {
    return (
      <Typography variant='h4' align='center' fontWeight={500}>
        Aguarde o processamento da confirmação do motorista
      </Typography>
    )
  }

  return (
    <S.Container>
      <ReceivementHeader
        title={(
          <S.SubTitle>
            Resumo do Recebimento{' '}
            <S.SubTitleExtension>- ARs e Lotes</S.SubTitleExtension>
          </S.SubTitle>
          )}
        headerButton={resolveHeaderButton()}
      />
      <ReceivementInfo
        operator={finish?.operator}
        supervisor={finish?.supervisor}
        dock={finish?.dock}
        supplier={finish?.supplier}
        receivementId={receivementId}
      />
      <ReceivementFinishFilters />

      {finish?.divergentProducts.length > 0 && (
      <>
        <S.TableTitle background={theme.colors.pink}>Lotes com devolução</S.TableTitle>
        {finish?.divergentProducts?.map((product, index) => (
          <ReceivementCard
            key={`${product.productDescription}-${index}`}
            product={product}
            BatchesTable={DivergentProductTable}
            headerColor={theme.colors.pink}
          />
        ))}
      </>
      )}

      {finish?.correctProducts.length > 0 && (
      <>
        <S.TableTitle background={theme.colors.green}>Lotes ok</S.TableTitle>
        {finish?.correctProducts?.map((product, index) => (
          <ReceivementCard
            key={`${product.productDescription}-${index}`}
            product={product}
            BatchesTable={CorrectProductTable}
            headerColor={theme.colors.green}
          />
        ))}
      </>
      )}
      {
        tags && tags.length > 0 && (
          <TagPrinterManagerPerBlock
            tagsToPrint={tags}
            showTagModal={showTagPrinterModal}
            setShowTagModal={setShowTagPrinterModal}
            actionAfterClose={() => setAlreadyPrinted(true)}
            modalTitle='Impressão de etiquetas de troca e/ou devolução'
            printLimit={25}
          />
        )
      }
    </S.Container>
  )
}

export default ReceivementFinish
